import type { AbilityKind } from '@bioforge/game-core';
import type { BossId, SoloAbility, VesselId } from '@bioforge/protocol';

/** Названия способностей для игрока (реальные медицинские термины, SPEC.md 15.1). */
export const ABILITY_NAMES: Readonly<Record<AbilityKind, string>> = {
  cryo: 'Криотерапия',
  antibiotic: 'Антибиотик',
  fever: 'Лихорадка',
  immunity: 'Иммунитет',
  scanner: 'Сканер',
};

/** Выбор способности перед игрой (SPEC.md 15.2). Хранится в памяти до перезагрузки страницы. */
export const loadout: {
  solo: 'cryo' | 'antibiotic';
  duelC: 'cryo' | 'antibiotic';
  duelV: 'fever' | 'immunity';
} = { solo: 'cryo', duelC: 'cryo', duelV: 'fever' };

export const BOSS_NAMES: Readonly<Record<BossId, string>> = {
  spornitsa: 'Спорница',
  grippozavr: 'Гриппозавр',
  vibrion: 'Вибрион-Спрут',
  tuberkul: 'Туберкул',
  chimera: 'Химера',
};

export const VESSEL_NAMES: Readonly<Record<VesselId, string>> = {
  jar: 'Банка',
  tube: 'Пробирка',
  petri: 'Чашка Петри',
  flask: 'Колба',
  bioreactor: 'Биореактор',
};

/** Отсек лаборатории, где идёт глава (сюжет, SPEC.md 19.2). */
export const CHAPTER_PLACES: Readonly<Record<VesselId, string>> = {
  jar: 'Кладовая',
  tube: 'Аналитическая лаборатория',
  petri: 'Инкубаторы',
  flask: 'Химический цех',
  bioreactor: 'Сердце Кузницы',
};

/** Сосуд в винительном падеже: «Очистите банку». */
export const VESSEL_ACCUSATIVE: Readonly<Record<VesselId, string>> = {
  jar: 'банку',
  tube: 'пробирку',
  petri: 'чашку Петри',
  flask: 'колбу',
  bioreactor: 'биореактор',
};

/** Кто босс — подпись под именем на панели боя (макет «Соло · Босс»). */
export const BOSS_TITLES: Readonly<Record<BossId, string>> = {
  spornitsa: 'Королева-плесень',
  grippozavr: 'Вирус гриппа',
  vibrion: 'Холерный вибрион',
  tuberkul: 'Микобактерия',
  chimera: 'Суперинфекция',
};

/** Повод для реплики босса в бою. */
export type Taunt = 'start' | 'hit' | 'miss' | 'low' | 'blocked' | 'sleep' | 'pause';

/** Реплики босса в бою (черновик, как и сюжет, SPEC.md 19.2). */
export const BOSS_TAUNTS: Readonly<Record<Taunt, string>> = {
  start: 'Добро пожаловать ко мне в гости, доктор!',
  hit: 'Ай! Ну погоди у меня!',
  miss: 'Мимо! Мои детки тебя задавят!',
  low: 'Нет-нет-нет! Детки, ко мне!',
  blocked: 'Не достанешь! Мои сторожа рядом.',
  sleep: 'Хр-р… Не будите меня…',
  pause: 'Испугался, доктор? Хи-хи!',
};

/** Как наносить урон боссу — по способу размещения (SPEC.md, 15.5). */
export const BOSS_DAMAGE_HINTS: Readonly<Record<'vessel' | 'outside' | 'hybrid', string>> = {
  vessel: 'Урон — очистки вплотную к боссу',
  outside: 'Урон — любая очистка в сосуде',
  hybrid: 'Урон — уничтожай его щупальца',
};

/** Что делает способность соло — коротко, для панели способности. */
export const SOLO_ABILITY_HINTS: Readonly<Record<SoloAbility, string>> = {
  cryo: 'Капсула зависает на несколько секунд',
  antibiotic: 'Уничтожает несколько бактерий',
};

/** Сосуд в предложном падеже: «босс в банке». */
export const VESSEL_PREPOSITIONAL: Readonly<Record<VesselId, string>> = {
  jar: 'банке',
  tube: 'пробирке',
  petri: 'чашке Петри',
  flask: 'колбе',
  bioreactor: 'биореакторе',
};

/** Способности Дуэли — коротко, для лобби и панели боя (SPEC.md 15.2). */
export const DUEL_ABILITY_HINTS: Readonly<
  Record<'cryo' | 'antibiotic' | 'fever' | 'immunity', string>
> = {
  cryo: 'Капсула зависает на несколько секунд',
  antibiotic: 'Уничтожает несколько бактерий',
  fever: 'Атака: у соперника капсулы падают быстрее',
  immunity: 'Защита: отражает следующую атаку',
};
